/* eslint-disable no-restricted-imports */
import {
  captureException as baseCaptureException,
  captureMessage as baseCaptureMessage,
  captureEvent as baseCaptureEvent,
} from '@sentry/react';

export {
  setTag,
  setTags,
  addBreadcrumb,
  init,
  setUser,
  setContext,
  getCurrentScope,
  browserTracingIntegration,
} from '@sentry/react';

/**
 * For backward compatibility with existing code but also convenience of not having to declare and import from a separate package
 */
export {
  CapabilityTier,
  CustomerImpact,
  EventClassification,
} from '@canvas/operational-types';

import {
  withCanvasEventMetadata,
  withCapabilityMetadata,
  type CanvasCaptureExceptionOptions,
  type CaptureExceptionForCapabilityOptions,
  type CapabilityInfo,
} from '@canvas/sentry-utils';

export type {
  CanvasCaptureExceptionOptions,
  CaptureExceptionForCapabilityOptions,
  CapabilityInfo,
};

type SentryCaptureExceptionParams = Parameters<typeof baseCaptureException>;
/**
 * Consider using {@link captureExceptionForCapability} since that will allow you to integrate more tightly with our
 * capability configs
 */
export const captureException = (
  value: SentryCaptureExceptionParams[0],
  options: CanvasCaptureExceptionOptions
) => {
  return baseCaptureException(value, withCanvasEventMetadata(options));
};

/**
 * Like {@link captureException} but will extract data from a capability config instead.
 * This data can be retrieved from @canvas/capability-summaries
 */
export const captureExceptionForCapability = (
  value: SentryCaptureExceptionParams[0],
  options: CaptureExceptionForCapabilityOptions,
  capabilityInfoListOrCapabilityInfo: CapabilityInfo[] | CapabilityInfo
) => {
  captureException(
    value,
    withCapabilityMetadata(options, capabilityInfoListOrCapabilityInfo)
  );
};

export const captureMessage = (
  message: Parameters<typeof baseCaptureMessage>[0],
  options: CanvasCaptureExceptionOptions
) => baseCaptureMessage(message, withCanvasEventMetadata(options));

export const captureMessageForCapability = (
  message: Parameters<typeof baseCaptureMessage>[0],
  options: CaptureExceptionForCapabilityOptions,
  capabilityInfoListOrCapabilityInfo: CapabilityInfo[] | CapabilityInfo
) =>
  captureMessage(
    message,
    withCapabilityMetadata(options, capabilityInfoListOrCapabilityInfo)
  );

export const captureEvent = (
  event: Parameters<typeof baseCaptureEvent>[0] & CanvasCaptureExceptionOptions
) => baseCaptureEvent(withCanvasEventMetadata(event));

export const captureEventForCapability = (
  event: Parameters<typeof baseCaptureEvent>[0] & CanvasCaptureExceptionOptions,
  capabilityInfoListOrCapabilityInfo: CapabilityInfo[] | CapabilityInfo
) => captureEvent(withCapabilityMetadata(event, capabilityInfoListOrCapabilityInfo));
